import { computed, ref } from 'vue'

class RealtimeService {
  constructor() {
    this.ws = null
    this.eventHandlers = new Map()
    this.pendingSubscriptions = []
    this.reconnectAttempts = ref(0)
    this.maxReconnectAttempts = 5
    this.reconnected = ref(false)
    this.reconnectDelay = 2000 // 2 seconds
    this.status = computed(() => {
      if (this.reconnected.value) return 'reconnected'
      if (this.reconnectAttempts.value >= this.maxReconnectAttempts && !this.isConnected())
        return 'failed'
      if (this.reconnectAttempts.value > 0) return 'reconnecting'

      return 'connected'
    })
    this.pingInterval = null // Add ping interval reference
    this.reconnectionTimeout = null
  }

  /**
   * Connect to the WebSocket server
   * @returns {Promise<void>}
   */
  connect() {
    return new Promise((resolve, reject) => {
      try {
        // Close existing connection if any
        if (this.ws) {
          this.disconnect()
        }

        // Create new WebSocket connection
        this.ws = new WebSocket(`${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/ws`)

        // Handle connection open
        this.ws.onopen = () => {
          this.startPing()
          resolve()
        }

        // Handle incoming messages
        this.ws.onmessage = (event) => {
          try {
            const { event: eventName, data } = JSON.parse(event.data)

            // Handle the initial connection message that contains our client ID
            if (eventName === 'connected') {
              // Process any pending subscriptions after we have our client ID
              this.processPendingSubscriptions()
              return
            }

            // Handle other events
            const handlers = this.eventHandlers.get(eventName)
            if (handlers) {
              handlers.forEach((handler) => handler(data))
            }
          } catch (error) {
            // allow console.error to catch this in Sentry
            // eslint-disable-next-line no-console
            console.error('Error processing WebSocket message:', error)
          }
        }

        // Handle connection error
        this.ws.onerror = (error) => {
          this.ws = null
          this.clearPing()
          reject(error)
        }

        // Handle connection close
        this.ws.onclose = () => {
          this.ws = null
          this.clearPing()
          this.eventHandlers.forEach((handlers, eventName) => {
            handlers.forEach((handler) => {
              this.subscribe(eventName, handler)
            })
          })
          this.attemptReconnect()
        }
      } catch (error) {
        reject(error)
      }
    })
  }

  /**
   * Attempt to reconnect to the WebSocket server
   */
  attemptReconnect() {
    if (this.reconnectAttempts.value <= this.maxReconnectAttempts) {
      this.reconnectAttempts.value++
      clearTimeout(this.reconnectionTimeout)

      this.reconnectionTimeout = setTimeout(() => {
        this.connect().then(() => {
          this.reconnected.value = true
          this.reconnectAttempts.value = 0
          clearTimeout(this.reconnectionTimeout)
          this.reconnectionTimeout = null

          setTimeout(() => {
            this.reconnected.value = false
          }, 2000)
        })
      }, this.reconnectDelay * this.reconnectAttempts.value)
    }
  }

  /**
   * Process any pending subscriptions that were queued while disconnected
   */
  processPendingSubscriptions() {
    while (this.pendingSubscriptions.length > 0) {
      const { eventName, handler } = this.pendingSubscriptions.shift()
      this._subscribe(eventName, handler)
    }
  }

  /**
   * Internal subscribe method that assumes connection is established
   * @param {string} eventName - Name of the event to subscribe to
   * @param {Function} handler - Function to handle the event
   */
  _subscribe(eventName, handler) {
    if (this.isConnected()) {
      this.ws.send(
        JSON.stringify({
          type: 'subscribe',
          eventName,
        }),
      )
    }

    // Store the handler
    if (!this.eventHandlers.has(eventName)) {
      this.eventHandlers.set(eventName, new Set())
    }
    this.eventHandlers.get(eventName).add(handler)
  }

  /**
   * Subscribe to an event
   * @param {string} eventName - Name of the event to subscribe to
   * @param {Function} handler - Function to handle the event
   */
  subscribe(eventName, handler) {
    if (!this.isConnected()) {
      // Queue the subscription if not connected or don't have client ID yet
      this.pendingSubscriptions.push({ eventName, handler })
      return
    }

    this._subscribe(eventName, handler)
  }

  /**
   * Disconnect from the WebSocket server
   */
  disconnect() {
    if (this.ws) {
      this.ws.close()
      this.ws = null
      this.clearPing()
      this.eventHandlers.clear()
    }
  }

  /**
   * Unsubscribe from an event
   * @param {string} eventName - Name of the event to unsubscribe from
   * @param {Function} handler - Specific handler to remove (optional)
   */
  unsubscribe(eventName, handler) {
    if (this.isConnected()) {
      this.ws.send(
        JSON.stringify({
          type: 'unsubscribe',
          eventName,
        }),
      )
    }

    if (handler) {
      // Remove specific handler
      const handlers = this.eventHandlers.get(eventName)
      if (handlers) {
        handlers.delete(handler)
        if (handlers.size === 0) {
          this.eventHandlers.delete(eventName)
        }
      }
    } else {
      // Remove all handlers for this event
      this.eventHandlers.delete(eventName)
    }
  }

  isConnected() {
    return this.ws !== null && this.ws?.readyState === WebSocket.OPEN
  }

  // Add ping logic
  startPing() {
    this.clearPing()
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.pingInterval = setInterval(() => {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(
            JSON.stringify({
              type: 'ping',
            }),
          )
        }
      }, 30000) // 30 seconds
    }
  }

  clearPing() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval)
      this.pingInterval = null
    }
  }
}

const realtimeServiceInstance = new RealtimeService()

export default realtimeServiceInstance

export { RealtimeService }
